/**
 * MARCAS DE PACIENTE (administración, 14/10/2026)
 * -----------------------------------------------
 * Las configura gestión en Configuración → «Marcas de paciente» (nombre,
 * texto en las citas, recargo en los presupuestos de cirugía, color y si está
 * activa). Ninguna viene en el código: sin ninguna configurada no sale ningún
 * botón. La regla de verdad vive en el servidor (services/marcas_paciente.py).
 *
 * Aquí, lo que comparten la ficha, la cita y el presupuesto.
 */
import { useEffect, useState } from 'react';
import { api } from './api';

/** El servidor contesta `{marcas: [...]}`; se acepta también una lista. */
export function listaDeMarcas(data) {
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.marcas) ? data.marcas : [];
}

export async function cargarMarcasPaciente({ todas = false } = {}) {
  const r = await api.get('/api/marcas-paciente', todas ? { params: { todas: true } } : undefined);
  return listaDeMarcas(r?.data);
}

/**
 * Las marcas ACTIVAS (o todas, para Configuración). Un fallo no rompe la
 * pantalla: sin la lista no se ofrecen botones, y el servidor sigue
 * aplicando sus efectos igual.
 */
export function useMarcasPaciente({ activo = true, todas = false, recarga = 0 } = {}) {
  const [marcas, setMarcas] = useState([]);
  useEffect(() => {
    if (!activo) return undefined;
    let vigente = true;
    Promise.resolve()
      .then(() => cargarMarcasPaciente({ todas }))
      .then((lista) => { if (vigente) setMarcas(lista); })
      .catch(() => { if (vigente) setMarcas([]); });
    return () => { vigente = false; };
  }, [activo, todas, recarga]);
  return marcas;
}

/** Las marcas que tiene el paciente, en el orden de la configuración. */
export function marcasDelPaciente(paciente, marcas) {
  const ids = Array.isArray(paciente?.marcas_paciente) ? paciente.marcas_paciente : [];
  return (marcas || []).filter((m) => ids.includes(m.id));
}

/** «Incluye recargo de A y B» (sin importes: el aviso es discreto). */
export function textoRecargo(nombres) {
  const lista = [...new Set((nombres || []).filter(Boolean))];
  if (!lista.length) return '';
  const unidos = lista.length === 1
    ? lista[0]
    : `${lista.slice(0, -1).join(', ')} y ${lista[lista.length - 1]}`;
  return `Incluye recargo de ${unidos}`;
}

/** Lo que dice un presupuesto ya creado (`recargos_aplicados`). */
export function textoRecargoDelPresupuesto(budget) {
  const r = Array.isArray(budget?.recargos_aplicados) ? budget.recargos_aplicados : [];
  return textoRecargo(r.map((x) => x.marca_nombre));
}

const normalizar = (s) => String(s || '').toLowerCase().normalize('NFD')
  .replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();

/** La hialuronidasa no es nunca la cirugía, aunque esté en el catálogo
 *  (el mismo trozo que mira el servidor: «hialuronid»). */
export function esLineaDeHialuronidasa(concepto) {
  return normalizar(concepto).includes('hialuronid');
}

/**
 * La línea de cirugía que el servidor detectaría solo
 * (`marcas_paciente.linea_de_cirugia`): el concepto es el nombre de una
 * cirugía del catálogo o empieza por él, y nunca la hialuronidasa. Devuelve
 * su índice en `items`, o -1. Sirve para PRESELECCIONAR la línea del recargo:
 * quien crea el presupuesto la confirma o la cambia.
 */
export function indiceLineaDeCirugia(items, cirugias) {
  const nombres = (cirugias || [])
    .map((s) => normalizar(s?.name))
    .filter((n) => n.length >= 3 && !n.includes('hialuronid'));
  return (items || []).findIndex((it) => {
    const c = normalizar(it?.concept);
    return c && !esLineaDeHialuronidasa(c) && nombres.some((n) => c === n || c.startsWith(n));
  });
}

export function llevaCirugiaDelCatalogo(items, cirugias) {
  return indiceLineaDeCirugia(items, cirugias) >= 0;
}
